import PageWrapper from "@components/layouts/PageWrapper";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { Toast } from "@helpers/popups/Toast";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import BackButton from "@components/buttons/BackButton";
import CustomSearchModalChooseField from "@components/form/CustomSearchModalChooseField";
import CustomTextAreaField from "@components/form/CustomTextAreaField";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@states/store";
import {
  addFormValues,
  addProductDetail,
  removeProductDetail,
  resetFormValues,
  removeAllProductDetail,
  updateProductDetail,
} from "@states/common/common.slice";
import CustomButton from "@components/buttons/CustomButton";
import { saleOrderSchema } from "@helpers/validation-schemas/primary-sale-order/sale-order.schema";
import CustomDateField from "@components/form/CustomDateField";
import useGetAllCustomers from "@hooks/customer/useGetAllCustomers";
import CustomSelectField from "@components/form/CustomSelectField";
import { useLazyGetProductByBarcodeQuery } from "@states/product/product.api";
import { TProduct } from "@type-defs/product/TProduct";
import CustomInputField from "@components/form/CustomInputField";
import { thousandSeperator } from "@utils/general/digit-separators";
import { PRIMARY_SALE_ORDER } from "@utils/constants/primary-sale-order.constants";
import {
  useGetAllPaymentTermsQuery,
  useGetWarehousesByUserQuery,
  useLazyGetProductDetailByWarehouseQuery,
  useGetAllSaleTypeQuery,
  useGetCustomerByUserQuery,
  useLazyGetAllPromotionNameListQuery,
  useLazyGetAllPromotionProductListQuery,
  useLazyGetAllWayWarehouseQuery,
} from "@states/common/common.api";
import { useLazyGetAllWarehouseProductListQuery } from "@states/common/common.api";

import useGetAllPaymentTypes from "@hooks/common/useGetAllPaymentTypes";
import { formatDBDate } from "@utils/general/format-db-date";
import { useDraggable } from "react-use-draggable-scroll";
import useGetAllProducts from "@hooks/product/useGetAllProducts";
import { LOCAL_STORAGE_KEYS } from "@utils/constants/local-storeage-keys.constants";
import { selectCurrentUser } from "@states/auth/auth.slice";
import { useCreateSecondarySaleOrderMutation } from "@states/secondary-sale-order/secondary-sale-order.api";
import DeleteButton from "@components/buttons/DeleteButton";
import PromotionIcon from "@assets/images/Icon.png";
import PromotionCard from "@components/extra/sales-order/PromotionCard";
import SaleOrderPromotionDetailModal from "@components/modals/sales-order/SaleOrderPromotionDetailModal";
import AuthComponent from "@components/auth/component";
import { roundedValue } from "@utils/general/celi-value";
import { SECONDARY_SALE_ORDER } from "@utils/constants/secondary-sale-order.constant";
import { DIRECT_SALES_ORDER } from "@utils/constants/direct-sales-order.constants";

type FormFields = z.infer<typeof saleOrderSchema>;

const DirectSaleAddPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [autoSelectedRangeByAmount, setAutoSelectedRangeByAmount] =
    useState("0");
  const [autoSelectedRangeByQty, setAutoSelectedRangeByQty]: any =
    useState("0");
  const [baseQty, setBaseQty]: any = useState(1);
  const [selectedPromotionItemList, setSelectedPromotionItemList] = useState(
    []
  );
  const [paymentTermDisabled, setPaymentTermDisabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const tableContainerRef = useRef<any>();
  const { events } = useDraggable(tableContainerRef);
  const [businessUnits, setBusinessUnits] = useState<any>([]);
  const [customerType, setCustomerType] = useState<any>();
  const [customerChannelId, setCustomerChannleId] = useState("");
  const [wayId, setWayId] = useState("");
  const user = useSelector(selectCurrentUser);
  const storedUser = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_KEY);
  const userData = user || (storedUser ? JSON.parse(storedUser!) : null);
  const { data: userWarehouseData, error: fetchUserWarehousesError } =
    useGetWarehousesByUserQuery({
      user_id: userData?.user_id,
    });
  const [
    getAllWayWarehouse,
    { data: wayWarehouseData, error: fetchWayWarehouseError },
  ] = useLazyGetAllWayWarehouseQuery();
  const { data: userCustomerData, error: fetchUserCustomerError } =
    useGetCustomerByUserQuery({
      user_id: userData?.user_id,
    });
  const { customerData } = useGetAllCustomers({ limit: 1000 });
  const { data: paymentTermData } = useGetAllPaymentTermsQuery({ status: 1 });
  const { paymentTypeData } = useGetAllPaymentTypes();
  const { data: saleTypeData } = useGetAllSaleTypeQuery();
  const { productData: productGetAllData } = useGetAllProducts({
    limit: 10000,
  });
  const [
    getProductByBarcode,
    { data: productData, isLoading: productLoading },
  ] = useLazyGetProductByBarcodeQuery();

  const [
    getProductDetailByWarehouse,
    {
      data: productDetailByWarehouseData,
      error: productDetailByWarehouseError,
      isLoading: productDetailByWarehouseLoading,
    },
  ] = useLazyGetProductDetailByWarehouseQuery();

  const [
    getAllPromotionProductList,
    {
      data: promotionProductData,
      error: promotionProductDataError,
      isLoading: promotionProductDataLoading,
    },
  ]: any = useLazyGetAllPromotionProductListQuery();
  const [
    getAllPromotionNameList,
    {
      data: PromotionData,
      error: getAllPromotioDataError,
      isLoading: promotioDataLoading,
    },
  ]: any = useLazyGetAllPromotionNameListQuery();
  const { formValues } = useSelector((state: RootState) => state.common);

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting }, //TODO: need to handle wrong field name errors, cause they are uncaught
    reset,
  } = useForm<FormFields>({
    resolver: zodResolver(saleOrderSchema),
  });

  const productBarcodeRef = useRef(null);

  const { append, remove, update, fields } = useFieldArray({
    control,
    name: "product_detail",
  });

  const [createPrimarySaleOrder, { isLoading, isSuccess, error: createError }] =
    useCreateSecondarySaleOrderMutation();

  const [getAllWarehouseProductList, { data: warehouseProductData }] =
    useLazyGetAllWarehouseProductListQuery();

  useErrorHandling(
    ...castTypeArr<TFailResponse>([
      createError,
      fetchUserWarehousesError,
      fetchWayWarehouseError,
      productDetailByWarehouseError,
      fetchUserCustomerError,
    ])
  );
  useEffect(() => {
    //@ts-ignore
    if (fetchUserCustomerError?.data?.success === false) {
      setIsDisabled(true);
    }
  }, [fetchUserCustomerError]);

  useEffect(() => {
    if (formValues?.payment_type_id === 3) {
      setPaymentTermDisabled(true);
      setValue("payment_terms_id", undefined);
    } else {
      setPaymentTermDisabled(false);
    }
  }, [formValues?.payment_type_id]);

  useEffect(() => {
    if (wayId) {
      getAllWayWarehouse({
        way_id: wayId,
      });
    }
  }, [wayId]);
  useEffect(() => {
    //@ts-ignore
    if (wayWarehouseData?.data?.length > 0) {
      getAllWarehouseProductList({
        //@ts-ignore
        warehouse_id: wayWarehouseData?.data?.[0]?.warehouse_id,
      });
    }
  }, [wayWarehouseData]);
  useEffect(() => {
    if (formValues?.customer_id && userCustomerData) {
      //@ts-ignore
      const customer = userCustomerData?.data.find(
        (c: any) => c.customer_id == formValues?.customer_id
      );
      if (customer) {
        setCustomerChannleId(customer.customer_category_id);
        setCustomerType(customer?.customer_type_id);
      }

      if (customer?.business_units && customer.business_units.length > 0) {
        setBusinessUnits(customer?.business_units || []);
      }
    }
  }, [formValues?.customer_id, userCustomerData]);

  useEffect(() => {
    if (formValues?.business_unit_id) {
      let filterBusinessUnit = businessUnits.find((bu: any) => {
        return bu.business_unit_id == formValues?.business_unit_id;
      });
      if (filterBusinessUnit?.way_id) {
        setWayId(filterBusinessUnit.way_id);
      }
    }
  }, [formValues?.business_unit_id, businessUnits]);

  useEffect(() => {
    if (wayId && customerChannelId && formValues?.sales_date) {
      console.log("start ");
      getAllPromotionNameList({
        customer_category_id: customerChannelId,
        way_id: wayId,
        start_date: formValues?.sales_date,
        type : 3
      });
    }
  }, [wayId, customerChannelId, formValues?.sales_date]);
  console.log("customerChannelId", customerChannelId);
  const resetForm = useCallback(() => {
    dispatch(resetFormValues());
  }, [dispatch]);

  useEffect(() => {
    if (formValues?.sale_promotion_id) {
      remove();
      dispatch(removeAllProductDetail([]));
      getAllPromotionProductList({
        sale_promotion_id: formValues?.sale_promotion_id,
      });
    }
  }, [formValues?.sale_promotion_id, removeAllProductDetail, remove]);

  useEffect(() => {
    function calcProductTotals(product: any) {
      const qty = Number(product.qty || 0);
      const base_qty = Number(product.base_qty || 1);
      const sale_price = Number(product.sales_price || 0);

      const gross_amount = qty * base_qty * sale_price;

      let discount_amount = 0;

      // Promotion discount
      if (product.is_promotion_item && product._promo_discount) {
        const promoDiscount = Number(product._promo_discount || 0);
        const promoDiscountType = product._promo_discount_type || "AMT";

        if (promoDiscountType === "%") {
          discount_amount = gross_amount * (promoDiscount / 100);
        } else {
          // AMT: multiply by qty * base_qty
          discount_amount = promoDiscount * qty * base_qty;
        }
      } else {
        // Normal discount
        const discountVal = Number(product.discount || 0);
        const discountType = product.discount_type || "AMT";

        if (discountType === "%") {
          discount_amount = gross_amount * (discountVal / 100);
        } else {
          discount_amount = Math.min(discountVal, gross_amount);
        }
      }

      // Tax should always apply on discounted amount
      const taxVal = Number(product.tax || 0);
      const taxType = product.tax_type || "%";
      let taxable_amount = gross_amount - discount_amount;

      const tax_amount =
        taxType === "%"
          ? taxable_amount * (taxVal / 100)
          : Math.min(taxVal, taxable_amount);

      const total_amount = gross_amount - discount_amount + tax_amount;

      return {
        amount: gross_amount,
        discount_amount,
        tax_amount,
        total_amount: isNaN(total_amount) ? 0 : total_amount,
      };
    }

    // Apply promotion logic
    function applyPromotionIfAny(product: any) {
      if (!product?.is_promotion_item) return calcProductTotals(product);

      const promo = product.is_promotion_item_list;
      if (!promo || !Array.isArray(promo.info))
        return calcProductTotals(product);

      const qty = Number(product.qty || 0);

      const appliedTier = promo.info.find(
        (info: any) =>
          Number(info.sale_range_from) <= qty &&
          qty <= Number(info.sale_range_to)
      );

      if (!appliedTier) {
        product.is_promotion_item = false;
        product._promo_discount = 0;
        product._promo_discount_type = 0;
        return calcProductTotals(product);
      }

      product._promo_discount = appliedTier.discount || 0;
      product._promo_discount_type = appliedTier.discount_type || 0;
      product._promo_bonus = appliedTier.bonus || 0;
      product._promo_cashback = appliedTier.cashback || 0;

      return calcProductTotals(product);
    }

    const subscription = watch((formValues, { name }) => {
      const { product_detail, ...theRest } = formValues;
      if (!Array.isArray(product_detail)) return;

      let totalBonus = 0;
      let totalCashback = 0;

      product_detail.forEach((prod, index) => {
        let updatedProduct = { ...prod };

        // Handle product change → reset details
        if (name?.includes(`product_detail.${index}.product_id`)) {
          //@ts-ignore
          const foundProduct = warehouseProductData?.data?.find(
            (p: any) => p.product_id === updatedProduct.product_id
          );

          if (foundProduct) {
            updatedProduct = {
              ...updatedProduct,
              product_id: foundProduct.product_id,
              unit_id: foundProduct.unit_id,
              unit_type_data: foundProduct.unit_type_data,
              sales_price: foundProduct.direct_sales_price,
              tax: foundProduct.direct_sales_tax,
              tax_type: foundProduct.direct_sales_tax_type || "AMT",
              available_qty: foundProduct.available_qty || 0,
              old_available_qty: foundProduct.available_qty || 0,
              qty: 0,
              discount: 0,
              discount_type: "AMT",
              amount: 0,
              total_amount: 0,
              _promo_discount: 0,
              _promo_bonus: 0,
              _promo_cashback: 0,
              is_promotion_item: false,
              is_promotion_item_list: null,
            };

            // Force reset visible fields in form state
            setValue(`product_detail.${index}.qty`, 0);
            setValue(`product_detail.${index}.amount`, 0);
            setValue(`product_detail.${index}.total_amount`, 0);
            setValue(`product_detail.${index}.discount`, 0);
            setValue(`product_detail.${index}.discount_type`, "AMT");
            setValue(`product_detail.${index}.is_promotion_item`, false);
            setValue(`product_detail.${index}.is_promotion_item_list`, null);
          }
        }

        // 🟡 Handle unit change → recalc available_qty
        if (
          name?.includes(`product_detail.${index}.unit_id`) ||
          name?.includes(`product_detail.${index}.product_id`)
        ) {
          const selectedUnit = updatedProduct.unit_type_data?.find(
            (u: any) => u.unit_id == updatedProduct.unit_id
          ) || { base_qty: 1 };
          const base_qty = Number(selectedUnit.base_qty) || 1;
          const oldQty = Number(updatedProduct.old_available_qty) || 0;

          updatedProduct.base_qty = base_qty;
          updatedProduct.available_qty =
            base_qty > 0 ? Math.floor(oldQty / base_qty) : oldQty;
        }

        // 🎁 Apply promotion if exists
        if (
          updatedProduct.product_id &&
          updatedProduct.unit_id &&
          promotionProductData
        ) {
          const promoDetail = promotionProductData.details.find(
            (d: any) =>
              d.product_id === updatedProduct.product_id &&
              d.unit_id === updatedProduct.unit_id
          );

          if (promoDetail) {
            updatedProduct.is_promotion_item = true;
            updatedProduct.is_promotion_item_list = { ...promoDetail };
          } else {
            updatedProduct.is_promotion_item = false;
            updatedProduct.is_promotion_item_list = null;
          }
        }

        // 🧾 Calculate totals
        const totals = applyPromotionIfAny(updatedProduct);
        updatedProduct = { ...updatedProduct, ...totals };

        // 🟢 Sum promotion bonuses & cashback
        //@ts-ignore
        totalBonus += Number(updatedProduct._promo_bonus || 0);
        //@ts-ignore
        totalCashback += Number(updatedProduct._promo_cashback || 0);

        // 🔄 Update each product
        //@ts-ignore
        update(index, updatedProduct);
        //@ts-ignore
        updateProductDetail({ index, product_detail: updatedProduct });
      });

      // 🧮 Calculate subtotal, tax, grand total
      const subTotal = product_detail.reduce(
        //@ts-ignore
        (acc, cur) => acc + (cur.total_amount || 0),
        0
      );

      const discountAmount =
        theRest.discount_type === "%"
          ? subTotal * (Math.min(theRest.discount || 0, 100) / 100)
          : Math.min(theRest.discount || 0, subTotal);

      const taxAmount =
        theRest.tax_type === "%"
          ? subTotal * ((theRest.tax || 0) / 100)
          : theRest.tax || 0;

      const otherCharges = theRest.other_charges || 0;

      dispatch(
        addFormValues({
          ...theRest,
          product_detail: product_detail.map((p) => ({ ...p })),
          sub_total: subTotal,
          discount_amount: discountAmount,
          discount: theRest.discount || 0,
          tax_amount: taxAmount,
          tax: theRest.tax || 0,
          other_charges: otherCharges,
          bonus: totalBonus,
          cash_back: totalCashback,
          grand_total_amount: Math.round(
            subTotal + otherCharges + taxAmount - discountAmount
          ),
        })
      );
    });

    return () => subscription.unsubscribe();
  }, [
    watch,
    dispatch,
    warehouseProductData,
    promotionProductData,
    getProductDetailByWarehouse,
    update,
    setValue,
  ]);

  const handleAppend = () => {
    append({
      amount: 0,
      discount: 0,
      api_level: "",
      discount_amount: 0,
      discount_type: "AMT",
      unit_type_data: [],
      product_id: "",
      qty: 0,
      available_qty: 0,
      tax: 0,
      tax_amount: 0,
      tax_type: "AMT",
      total_amount: 0,
      sales_price: 0,
      unit_id: "",
      unit_name: "",
      product_code: "",
      unit_type_id: "",
      product_name: "",
      warehouse_id: "",
      warehouse_name: "",
    });
  };

  const handleRemove = (index: number) => {
    remove(index);
    dispatch(removeProductDetail(index));
  };

  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const barcode = e.target.value;

    getProductByBarcode({ barcode });
  };

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      // async stuff here
      // console.log(data, 'data');
      await asyncDispatcher(data);
    } catch (error: any) {
      setError("root", {
        message: error.message,
      });
    }
  };

  console.log("Errors", errors);
  console.log("ffffff=>>>", formValues);

  const asyncDispatcher = useCallback(
    async (reqBody: any) => {
      try {
        const {
          product_detail,
          payment_type_id,
          sale_type_id,
          payment_terms_id,
          sales_date,
          delivery_date,
          ...rest
        } = formValues;

        const formattedProductDetails = product_detail?.map((pd: any) => ({
          product_id: pd.product_id,
          unit_id: pd.unit_id,
          //@ts-ignore
          warehouse_id: wayWarehouseData?.data?.[0]?.warehouse_id,
          sales_price: pd.sales_price,
          qty: pd.qty,
          amount: pd.amount,
          discount: pd.discount,
          discount_type: pd.discount_type,
          discount_amount: pd.discount_amount,
          is_promotion_item: pd.is_promotion_item,
          sale_promotion_detail_id:
            pd?.is_promotion_item_list?.sale_promotion_detail_id,
          tax: pd.tax,
          tax_type: pd.tax_type,
          tax_amount: pd.tax_amount,
          total_amount: pd.total_amount,
          available_qty: pd.available_qty,
        }));
        if (!formattedProductDetails) {
          await Toast.fire({
            title: "Please add at least one product detail.",
            icon: "error",
          });
        } else {
          console.log("formattedProductDetails==>", product_detail);
          console.log("Form", {
            ...rest,
            payment_type_id,
            sale_type_id,

            sales_date: formatDBDate(new Date(sales_date)),
            delivery_date: formatDBDate(new Date(delivery_date)),
            payment_terms_id:
              payment_type_id === 3 ? undefined : payment_terms_id,
            product_detail: formattedProductDetails,
          });

          const res = await createPrimarySaleOrder({
            ...rest,
            payment_type_id,
            sale_type_id,
            type: "Direct",
            sales_date: formatDBDate(new Date(sales_date)),
            delivery_date: formatDBDate(new Date(delivery_date)),

            payment_terms_id:
              payment_type_id === 3 ? undefined : payment_terms_id,
            product_detail: formattedProductDetails,
          });
          const successData = "data" in res ? res.data : null;
          const { success, message } = successData as TSuccessResponse;
          if (success || isSuccess) {
            await Toast.fire({
              title: message,
              icon: "success",
            });
            setRedirecting(true);
            navigate(DIRECT_SALES_ORDER.BASE_PATH, {
              state: {
                reload: true,
              },
            });
          }
        }
      } catch (error) {
        console.log("Error", error);
      }
    },
    [createPrimarySaleOrder, isSuccess, Toast, navigate, formValues]
  );

  return (
    <AuthComponent
      action="create"
      module="Direct Sale Order"
      subModule="Direct Sale Order"
      page
    >
      <SaleOrderPromotionDetailModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        data={selectedPromotionItemList}
        amount={autoSelectedRangeByAmount}
        qty={autoSelectedRangeByQty}
        base_qty={baseQty}
      />
      <PageWrapper>
        {(isLoading ||
          redirecting ||
          productDetailByWarehouseLoading ||
          watchLoading) && <LoadingSpinner />}
        <BackButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <h3 className="mb-4 ml-2 text-2xl font-[600] text-primary-dark uppercase">
            Add New Sale Order
          </h3>
          <div className="grid grid-cols-6 gap-x-4 gap-y-8">
            <CustomDateField
              required
              errors={errors}
              name="sales_date"
              label="Sale Date"
              register={register}
              key="sales_date"
              //@ts-ignore
              control={control}
              disabled={isDisabled}
            />
            <CustomDateField
              required
              errors={errors}
              name="delivery_date"
              label="Delivery Date"
              register={register}
              key="delivery_date"
              //@ts-ignore
              control={control}
              disabled={isDisabled}
            />
            <CustomSelectField
              required
              errors={errors}
              name="sale_type_id"
              label="Sale Type"
              placeHolder="Select Sale type"
              //@ts-ignore
              control={control}
              options={saleTypeData?.map((item: any) => ({
                label: item.sale_type_name,
                value: item.sale_type_id,
              }))}
              disabled={isDisabled}
            />
            <CustomSearchModalChooseField
              required
              key={"customer_id"}
              errors={errors}
              colSpan={2}
              name={"customer_id"}
              label="Customer Name"
              title={"Customer List"}
              disabled={isDisabled}
              columns={[
                {
                  name: "customer_name",
                  columnName: "Customer Name",
                },
                {
                  name: "phone_number",
                  columnName: "Phone Number",
                },
              ]}
              register={register}
              // borderColor={field.borderColor}
              placeHolder="Choose customer name"
              setValue={setValue}
              //@ts-ignore
              data={userCustomerData?.data?.map((customer: any) => ({
                customer_id: customer.customer_id,
                customer_name:
                  customer.customer_first_name +
                  " " +
                  (customer.customer_last_name || " "),
                phone_number: customer.customer_phone1,
              }))}
              columnName={"Customer Name"}
              idName={"customer_id"}
              // value={field.value}
              nameName={"customer_name"}
            />
            <CustomSelectField
              disabled={getValues("customer_id") === undefined || isDisabled}
              errors={errors}
              name="business_unit_id"
              label="Business Unit"
              placeHolder="Select business unit"
              //@ts-ignore
              control={control}
              options={businessUnits?.map((bu: any) => ({
                label: bu.business_unit_name,
                value: bu.business_unit_id,
              }))}
              required
            />
            <CustomSelectField
              required
              errors={errors}
              name="payment_type_id"
              label="Payment Type"
              placeHolder="Select payment type"
              //@ts-ignore
              control={control}
              options={paymentTypeData
                ?.filter((item: any) => {
                  // For customerType 1: Include only "CashDown"
                  if (customerType == 1) {
                    return item.payment_type_name == "Cash Down";
                  }
                             if (customerType == 2) {
                    // For customerType 2: Allow both "Cash Down" and "Credit"
                        return (
                          item.payment_type_name == "Cash Down" ||
                          item.payment_type_name == "Credit"
                        );
                  }
                  // Default: Exclude "Consignment"
                  return item.payment_type_name != "Consignment";
                })
                .map((item: any) => ({
                  label: item.payment_type_name,
                  value: item.payment_type_id,
                }))}
              disabled={isDisabled}
            />
            <CustomSelectField
              disabled={paymentTermDisabled || isDisabled}
              errors={errors}
              name="payment_terms_id"
              label="Payment Term"
              placeHolder="Select payment term"
              //@ts-ignore
              control={control}
              options={paymentTermData?.map((item: any) => ({
                label: item.payment_terms,
                value: item.payment_terms_id,
              }))}
              value={
                formValues?.payment_terms_id
                  ? formValues?.payment_terms_id
                  : undefined
              }
              required={formValues?.payment_type_id !== 3}
            />
            {/*                     
                    <CustomTextAreaField
                        value={formValues?.remark}
                        key={'remark'}
                        colSpan={3}
                        errors={errors}
                        name="remark"
                        register={register}
                    /> */}
            {PromotionData && PromotionData.length > 0 && (
              <CustomSelectField
                errors={errors}
                label="Promotion Name"
                name="sale_promotion_id"
                placeHolder="Select Promotion Name"
                //@ts-ignore
                control={control}
                options={PromotionData?.map((sp: any) => ({
                  label: sp.promotion_name,
                  value: sp.sale_promotion_id,
                }))}
                required
              />
            )}
            <CustomTextAreaField
              disabled={isDisabled}
              value={formValues?.description}
              key={"description"}
              errors={errors}
              colSpan={6}
              name="description"
              register={register}
            />
            <div className="flex justify-between col-span-6">
              <h3 className="ml-2 font-[600] text-primary-dark">
                <span className=" text-[1.1rem]">Add Sale Order Detail</span>
              </h3>
            </div>
            <div className="flex justify-between col-span-6">
              <h3 className="ml-2 font-[600] text-primary-dark">
                <span className=" text-[1.1rem]">Product Detail</span>
                <span className="ml-1 text-red-500 text-[1.1rem]">*</span>
              </h3>
              {isDisabled ? (
                <h4 className="mr-4 cursor-pointer font-[500] text-primary-dark text-[1rem]">
                  Add More Product +
                </h4>
              ) : (
                <h4
                  onClick={handleAppend}
                  className="mr-4 cursor-pointer font-[500] text-primary-dark text-[1rem]"
                >
                  Add More Product +
                </h4>
              )}
            </div>
            <div className="-mt-4 col-span-full">
              <div
                ref={tableContainerRef}
                {...events}
                className="w-[100%] mx-auto cursor-grab active:cursor-grabbing overflow-x-auto hide-scrollbar"
              >
                <table className="min-w-full overflow-x-scroll custom-table">
                  <thead className="h-12 py-3 text-xs font-light text-netural-600 bg-neutral-100">
                    <th className="text-xs font-light 2xl:text-sm ">No</th>
                    <th className="text-xs font-light 2xl:text-sm ">
                      Product Name
                    </th>

                    <th className="text-xs font-light 2xl:text-sm">Unit</th>
                    <th className="text-xs font-light 2xl:text-sm">
                      Sales Price
                    </th>
                    <th className="text-xs font-light 2xl:text-sm">Qty</th>
                    <th className="text-xs font-light text-nowrap 2xl:text-sm">
                      Available Qty
                    </th>
                    <th className="text-xs font-light 2xl:text-sm">Amount</th>
                    <th className="text-xs font-light 2xl:text-sm">Discount</th>
                    <th className="text-xs font-light 2xl:text-sm">Tax</th>
                    <th className="text-xs font-light 2xl:text-sm">
                      Total Amount
                    </th>
                    <th className="text-xs font-light 2xl:text-sm">Action</th>
                  </thead>
                  <tbody className="text-center">
                    {formValues?.product_detail?.length > 0 ? (
                      formValues?.product_detail?.map(
                        (product_detail: any, i: number) => {
                          const options = product_detail?.unit_type_data.map(
                            (unit: any) => {
                              return {
                                label: unit.unit_name,
                                value: unit.unit_id,
                              };
                            }
                          );

                          let baseUnit = (product_detail?.unit_type_data ?? []).find(
                            (unit: any) => {
                              return unit.unit_id == product_detail?.unit_id;
                            }
                          );
                          let base_qty = (baseUnit && baseUnit.base_qty) || 1;
                          return (
                            <tr key={i}>
                              {/* <td>{baseUnit && baseUnit.base_qty}</td> */}
                              {product_detail?.is_promotion_item === true ? (
                                <td>
                                  <div className="w-[120px] flex justify-center items-center ">
                                    <img
                                      src={PromotionIcon}
                                      alt="promotion_icon"
                                      className=" object-cover cursor-pointer"
                                      onClick={() => {
                                        setSelectedPromotionItemList(
                                          product_detail?.is_promotion_item_list ||
                                            []
                                        );
                                        setAutoSelectedRangeByAmount(
                                          product_detail?.amount
                                        );
                                        setAutoSelectedRangeByQty(
                                          product_detail?.qty
                                        );
                                        setBaseQty(
                                          product_detail?.base_qty || 1
                                        );
                                        setIsModalOpen(true);
                                      }}
                                    />
                                  </div>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              <td>
                                <CustomSelectField
                                  options={
                                    //@ts-ignore
                                    warehouseProductData &&
                                    //@ts-ignore
                                    warehouseProductData?.data?.length > 0 &&
                                    //@ts-ignore
                                    warehouseProductData?.data?.map(
                                      //@ts-ignore
                                      (product) => {
                                        return {
                                          label: ` (
                                            ${product.product_code_prefix}${product.product_code}-${product.product_name})`,
                                          value: product.product_id,
                                        };
                                      }
                                    )
                                  }
                                  classNames="w-[100%] min-w-[350px]"
                                  noLabel
                                  errors={errors}
                                  name={`product_detail.${i}.product_id`}
                                  //@ts-ignore
                                  control={control}
                                />
                                {/* @ts-ignore */}
                                {errors?.product_detail?.[i].product_id && (
                                  <div className="mt-2 font-[300] italic text-[0.7rem] text-red-600">
                                    {
                                      errors?.product_detail?.[i]?.product_id
                                        ?.message
                                    }
                                  </div>
                                )}
                              </td>

                              <td>
                                <CustomSelectField
                                  options={
                                    product_detail?.unit_type_data &&
                                    product_detail?.unit_type_data.map(
                                      (unit: any) => {
                                        return {
                                          label: unit.unit_name,
                                          value: unit.unit_id,
                                        };
                                      }
                                    )
                                  }
                                  classNames="w-[100%] min-w-[300px]"
                                  noLabel
                                  errors={errors}
                                  name={`product_detail.${i}.unit_id`}
                                  //@ts-ignore
                                  control={control}
                                  value={product_detail?.unit_id}
                                  defaultValue={product_detail?.unit_id}
                                />
                              </td>
                              <td>
                                <CustomInputField
                                  readOnly
                                  disabled
                                  noLabel
                                  classNames="w-[100%]"
                                  errors={errors}
                                  name={`product_detail.${i}.sales_price`}
                                  register={register}
                                  setValue={setValue}
                                  value={product_detail.sales_price}
                                />
                              </td>
                              <td>
                                {/* <Controller
                                  name={`product_detail.${i}.qty`}
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="text"
                                      min={0}
                                      max={product_detail.available_qty || 0}
                                      className="min-w-[100px] w-[100%] px-4 py-3 text-black border-[0.7px] focus:ring-1 ring-primary focus:outline-none rounded border-primary-light"
                                      placeholder="Qty"
                                      onChange={(e) => {
                                        field.onChange(e);
                                        if (product_detail.is_promo_item) {
                                          setValue(
                                            `product_detail.${i}.discount`,
                                            e.target.value
                                              ? product_detail._promo_discount
                                              : 0
                                          );
                                        } else {
                                          setValue(
                                            `product_detail.${i}.discount`,
                                            e.target.value
                                              ? product_detail.discount
                                              : 0
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                /> */}

                                <Controller
                                  name={`product_detail.${i}.qty`}
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="number"
                                      min={0}
                                      max={product_detail.available_qty || 0}
                                      className="min-w-[100px] w-[100%] px-4 py-3 text-black border-[0.7px] focus:ring-1 ring-primary focus:outline-none rounded border-primary-light"
                                      placeholder="Qty"
                                      onChange={(e) => {
                                         let value = Number(e.target.value);

                                        // Clamp value between 0 and available_qty
                                        const maxQty = product_detail.available_qty || 0;
                                        if (value < 0) value = 0;
                                        if (value > maxQty) value = maxQty;
                                        
                                        field.onChange(e);
                                        if (product_detail.is_promo_item) {
                                          setValue(
                                            `product_detail.${i}.discount`,
                                            e.target.value
                                              ? product_detail._promo_discount
                                              : 0
                                          );
                                        } else {
                                          setValue(
                                            `product_detail.${i}.discount`,
                                            e.target.value
                                              ? product_detail.discount
                                              : 0
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />

                                {/* @ts-ignore */}
                                {errors?.product_detail?.[i].qty && (
                                  <div className="mt-2 font-[300] italic text-[0.7rem] text-red-600">
                                    {errors?.product_detail?.[i]?.qty?.message}
                                  </div>
                                )}
                              </td>
                              <td className="px-2">
                                <div
                                  className={`px-8 py-3 text-primary-dark border-[0.7px] w-[100px] focus:ring-1 
                                                                   bg-zinc-100 cursor-not-allowed
                                                                        ring-primary focus:outline-none rounded border-primary-light
                                                                `}
                                >
                                  {product_detail.available_qty}
                                </div>
                              </td>
                              <td>
                                <CustomInputField
                                  disabled
                                  inputType="number"
                                  noLabel
                                  classNames="w-[100%]"
                                  errors={errors}
                                  name={`product_detail.${i}.amount`}
                                  register={register}
                                  setValue={setValue}
                                  value={product_detail.amount}
                                />
                              </td>
                              <td>
                                <div className="flex items-center">
                                  {product_detail.is_promotion_item === true ? (
                                    <CustomInputField
                                      setValue={setValue}
                                      inputType="text"
                                      classNames="-mr-3 w-[120px] px-4"
                                      noLabel
                                      errors={errors}
                                      name={`product_detail.${i}.discount`}
                                      register={register}
                                      defaultValue={product_detail.discount}
                                      value={product_detail._promo_discount}
                                      min={0}
                                      disabled={
                                        product_detail?.is_promotion_item ===
                                        true
                                      }
                                    />
                                  ) : (
                                    <CustomInputField
                                      setValue={setValue}
                                      inputType="text"
                                      classNames="-mr-3 w-[120px] px-4"
                                      noLabel
                                      errors={errors}
                                      name={`product_detail.${i}.discount`}
                                      register={register}
                                      defaultValue={product_detail.discount}
                                      value={product_detail.discount}
                                      min={0}
                                      disabled={
                                        product_detail?.is_promotion_item ===
                                        true
                                      }
                                    />
                                  )}
                                  <select
                                    disabled={
                                      product_detail?.is_promotion_item === true
                                    }
                                    value={
                                      product_detail?.is_promotion_item === true
                                        ? product_detail._promo_discount_type
                                        : product_detail.discount_type
                                    }
                                    onChange={(e) =>
                                      setValue(
                                        `product_detail.${i}.discount_type`,
                                        e.target.value
                                      )
                                    }
                                    className={`px-2 mx-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                                  >
                                    <option value="AMT">AMT</option>
                                    <option value="%">%</option>
                                  </select>
                                </div>
                              </td>
                              <td>
                                <div className="flex items-center">
                                  <CustomInputField
                                    disabled
                                    setValue={setValue}
                                    inputType="number"
                                    classNames="-mr-3 w-[130px]"
                                    noLabel
                                    errors={errors}
                                    name={`product_detail.${i}.tax`}
                                    register={register}
                                    defaultValue={product_detail.tax}
                                    value={product_detail.tax}
                                    min={0}
                                  />
                                  <select
                                    disabled
                                    value={product_detail.tax_type || "AMT"}
                                    onChange={(e) =>
                                      setValue(
                                        `product_detail.${i}.tax_type`,
                                        e.target.value
                                      )
                                    }
                                    className={`px-2 ml-2 mr-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                                  >
                                    <option value="AMT">AMT</option>
                                    <option value="%">%</option>
                                  </select>
                                </div>
                              </td>

                              <td>
                                <CustomInputField
                                  inputType="number"
                                  setValue={setValue}
                                  noLabel
                                  errors={errors}
                                  name={`product_detail.${i}.total_amount`}
                                  register={register}
                                  value={product_detail?.total_amount}
                                  disabled
                                />
                              </td>
                              <td>
                                <div
                                  style={{
                                    width: "100px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <DeleteButton
                                    // disabled={muted}
                                    id={i}
                                    actionHandler={() => handleRemove(i)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )
                    ) : (
                      <></>
                      // <tr>
                      //     <td colSpan={5} className="text-red-600 text-center italic text-sm">
                      //         Product Detail is required
                      //     </td>
                      // </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="w-[48%] 2xl:w-[36%] mt-8 float-left">
                {promotionProductData &&
                  formValues?.product_detail?.length > 0 && (
                    <PromotionCard
                      promotionProductData={promotionProductData}
                      bonus={formValues?.bonus}
                      cash_back={formValues?.cash_back}
                    />
                  )}
              </div>
              {formValues?.product_detail?.length > 0 && (
                <div className="bg-zinc-100 rounded w-[48%] 2xl:w-[36%] mt-8 float-right p-6">
                  <div className="flex items-center mb-4">
                    <h5 className="text-[1.05rem] basis-3/12">Sub Total</h5>
                    <span className="s basis-1/12">:</span>
                    <div className="flex items-center basis-6/12"></div>
                    <h5 className="text-right basis-2/12 text-[1.05rem]">
                      {thousandSeperator(formValues.sub_total || 0)}
                    </h5>
                  </div>
                  <div className="flex items-center mb-2">
                    <h5 className=" basis-3/12 text-[1.05rem]">Discount</h5>
                    <span className=" basis-1/12">:</span>
                    <div className="flex items-center basis-6/12">
                      <CustomInputField
                        defaultValue={0}
                        classNames="-mr-3 w-[130px]"
                        noLabel
                        inputType="number"
                        errors={errors}
                        name={"discount"}
                        placeHolder=" "
                        register={register}
                        min={0}
                        //@ts-ignore
                        max={
                          formValues?.discount_type == "%"
                            ? //@ts-ignore
                              100
                            : formValues?.sub_total
                        }
                      />
                      <select
                        {...register("discount_type")}
                        className={`px-2 ml-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                      >
                        <option selected value="AMT">
                          AMT
                        </option>
                        <option value="%">%</option>
                      </select>
                    </div>
                    <h5 className="text-right basis-2/12 text-[1.05rem]">
                      -
                      {thousandSeperator(
                        formValues.discount_amount.toFixed(2) || 0
                      )}
                    </h5>
                  </div>
                  <div className="flex items-center mb-2">
                    <h5 className=" basis-3/12 text-[1.05rem]">Tax</h5>
                    <span className=" basis-1/12">:</span>
                    <div className="flex items-center basis-6/12">
                      <CustomInputField
                        defaultValue={0}
                        classNames="-mr-3 w-[130px]"
                        noLabel
                        inputType="number"
                        errors={errors}
                        name={"tax"}
                        placeHolder=" "
                        register={register}
                        min={0}
                      />
                      <select
                        {...register("tax_type")}
                        className={`px-2 ml-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                      >
                        <option selected value="AMT">
                          AMT
                        </option>
                        <option value="%">%</option>
                      </select>
                    </div>
                    <h5 className="text-right basis-2/12 text-[1.05rem]">
                      {thousandSeperator(formValues.tax_amount?.toFixed(2) || 0)}
                    </h5>
                  </div>
                  <div className="flex items-center mb-4">
                    <h5 className=" basis-3/12 text-[1.05rem]">
                      Other Charges
                    </h5>
                    <span className=" basis-1/12">:</span>
                    <div className="flex items-center basis-6/12">
                      <CustomInputField
                        defaultValue={0}
                        classNames="-mr-3 w-[200px]"
                        noLabel
                        inputType="number"
                        errors={errors}
                        name={"other_charges"}
                        placeHolder=" "
                        register={register}
                      />
                    </div>
                    <h5 className="text-right basis-2/12 text-[1.05rem]">
                      {thousandSeperator(formValues.other_charges || 0)}
                    </h5>
                  </div>
                  <div className="flex items-center mb-2">
                    <h5 className="text-xl font-semibold basis-3/12">
                      Grand Total
                    </h5>
                    <span className=" basis-1/12">:</span>
                    <div className="flex items-center basis-6/12"></div>
                    <h5 className="text-xl font-semibold text-right basis-2/12 text-primary-dark">
                      {thousandSeperator(
                        formValues.grand_total_amount.toFixed(2) || 0
                      )}
                    </h5>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="ml-2 mt-14">
            <CustomButton
              isLarge
              title="Cancel"
              color="white"
              textColor="gray.800"
              className="mr-10"
              handleClick={() => navigate(-1)}
            />
            <CustomButton
              handleClick={handleSubmit(onSubmit)}
              isLarge
              title={isSubmitting ? "Loading..." : "Submit"}
              disabled={isSubmitting || isDisabled}
              type="submit"
            />
          </div>
          {errors.root && (
            <div className="mt-6 text-red-500">{errors.root.message}</div>
          )}
        </form>
      </PageWrapper>
    </AuthComponent>
  );
};

export default DirectSaleAddPage;
